import { sectionForCompletenessPiece } from '../services/deliveryWorkspace';
import { PIECE_STATUS } from '../services/programCompleteness';
import { summarizePlanDeliveryContract } from '../services/planDeliveryContract';

const STATUS_LABELS = Object.freeze({
  [PIECE_STATUS.READY]: 'Prêt',
  [PIECE_STATUS.PARTIAL]: 'À compléter',
  [PIECE_STATUS.TODO]: 'À préparer',
  [PIECE_STATUS.NA]: 'Non requis',
});

/**
 * Quand la preuve est refusée, la cause la plus fréquente est une anamnèse
 * modifiée après la validation du plan : la preuve scelle aussi l'anamnèse.
 * On le dit, plutôt que de laisser chercher.
 */
function causeProbable(client) {
  const valideLe = Date.parse(client?.journey_state?.plan_validation?.validated_at || '');
  const modifieeLe = Date.parse(client?.updatedAt || client?.updated_at || '');
  if (!Number.isFinite(valideLe) || !Number.isFinite(modifieeLe)) return null;
  if (modifieeLe <= valideLe) return null;
  return 'L’anamnèse a été modifiée après la validation du plan. La preuve scelle aussi '
    + 'l’anamnèse : il faut donc revalider avant de pouvoir publier.';
}

export default function DeliveryAppContentSummary({
  state,
  client = null,
  error = null,
  onOpenPreview,
  onRevalidate,
  revalidating = false,
}) {
  if (state?.state === 'loading') {
    return <div className="jrn-surface jrn-surface--quiet">Lecture de la version validée…</div>;
  }
  if (state?.state === 'error') {
    return <div className="jrn-error" role="alert">{state.message}</div>;
  }
  if (state?.state === 'stale') {
    return (
      <article className="jrn-delivery-content">
        <header className="jrn-delivery-content__head">
          <div>
            <p className="jrn-delivery-content__eyebrow">Contenu mis à jour</p>
            <h4>Les changements doivent être revalidés</h4>
            <p>
              Les menus, recettes, compléments ou le Meal Prep ont changé depuis
              la dernière validation. Rien n’est perdu et rien n’a été publié.
            </p>
            {/* Quand on arrive ici après un clic sur « Publier », il faut le
                dire : sinon Anissa croit avoir publié et recommence en boucle. */}
            {state.stoppedPublication && (
              <p className="jrn-delivery-content__stopped">
                <strong>La publication vient d’être arrêtée pour cette raison.</strong>{' '}
                Revalide le contenu ci-dessous, puis republie.
              </p>
            )}
          </div>
        </header>
        {causeProbable(client) && (
          <p className="jrn-delivery-content__cause">
            <strong>Cause probable.</strong> {causeProbable(client)}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="jrn-btn jrn-btn--soft" onClick={() => onOpenPreview()}>
            Vérifier le contenu
          </button>
          <button
            type="button"
            className="jrn-btn jrn-btn--primary"
            onClick={onRevalidate}
            disabled={revalidating || typeof onRevalidate !== 'function'}
          >
            {revalidating ? 'Revalidation…' : 'Revalider le contenu de l’app'}
          </button>
        </div>
        {/* L'échec de la revalidation s'affichait tout en bas de la section,
            très loin du bouton : personne ne le voyait. Il reste ici. */}
        {error && (
          <p className="jrn-delivery-content__echec" role="alert">
            <strong>La revalidation a échoué.</strong> {error}
          </p>
        )}
      </article>
    );
  }
  if (state?.state !== 'ready' || !state.contract) return null;
  const summary = summarizePlanDeliveryContract(state.contract);
  return (
    <article className="jrn-delivery-content">
      <header className="jrn-delivery-content__head">
        <div>
          <p className="jrn-delivery-content__eyebrow">Version extraite à la validation</p>
          <h4>Contenu réel de l’application</h4>
          <p>
            Le plan validé reste la source unique. Les recettes et finitions ci-dessous
            peuvent être complétées dans l’aperçu, puis seront enregistrées dans le SaaS.
          </p>
        </div>
        <span className="jrn-delivery-content__seal">✓ Version liée</span>
      </header>
      <div className="jrn-delivery-content__stats">
        <div><strong>{summary.meals.length}</strong><span>repères repas / jour</span></div>
        <div><strong>{summary.readiness.summary.fridge_items}</strong><span>repères frigo</span></div>
        <div><strong>{summary.readiness.summary.supplement_groups}</strong><span>moments compléments</span></div>
      </div>
      <div className="jrn-delivery-content__meals">
        {summary.meals.map((meal) => (
          <div key={meal.slot} className="jrn-delivery-content__meal">
            <span>{meal.label}</span>
            <strong>{meal.title}</strong>
            <small>{meal.alternatives > 0
              ? `${meal.alternatives} alternative${meal.alternatives > 1 ? 's' : ''}`
              : 'Alternatives à préparer dans l’aperçu'}</small>
          </div>
        ))}
      </div>
      {/* Ces lignes étaient inertes : Anissa lisait « Compléments : à
          compléter » puis devait retrouver l'endroit toute seule sous un
          unique bouton « Ouvrir l'aperçu complet ». Chaque ligne qui a une
          section mène désormais directement au bon écran. */}
      <ul className="jrn-delivery-content__pieces">
        {summary.pieces.map((piece) => {
          const section = sectionForCompletenessPiece(piece.key);
          const corps = (
            <>
              <span>{piece.label}</span>
              <strong>{STATUS_LABELS[piece.status] || piece.status}</strong>
              {piece.detail && <small>{piece.detail}</small>}
            </>
          );
          if (!section || typeof onOpenPreview !== 'function') {
            return <li key={piece.key} data-status={piece.status}>{corps}</li>;
          }
          return (
            <li key={piece.key} data-status={piece.status}>
              <button
                type="button"
                className="jrn-delivery-content__piece-btn"
                onClick={() => onOpenPreview(section)}
              >
                {corps}
                <span className="jrn-delivery-content__piece-go" aria-hidden="true">Modifier ›</span>
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="jrn-btn jrn-btn--primary" onClick={() => onOpenPreview()}>
        Ouvrir l’aperçu complet et préparer les recettes →
      </button>
    </article>
  );
}
